import { breakpointKeys, type ResponsiveVariant } from "@octopus-energy/coral/atoms"
import { type CoralBreakpoint } from "@octopus-energy/coral-design"
import { isDefined, merge, pickBy } from "remeda"

/**
 * Checks if a value is a responsive object (has breakpoint keys like base, md, lg, etc.)
 */
function isResponsiveProps<TProps>(
    value: unknown
): value is Partial<Record<CoralBreakpoint | "base", TProps>> {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return false
    }

    const keys = Object.keys(value)

    return keys.some((key) => key === "base" || breakpointKeys.includes(key as CoralBreakpoint))
}

/**
 * Merges a single responsive prop value, where the override only replaces
 * the default if the override value is not undefined.
 */
function mergeResponsiveValue<TValue>(
    defaultValue: ResponsiveVariant<TValue>,
    overrideValue?: ResponsiveVariant<TValue>
): ResponsiveVariant<TValue> | undefined {
    if (overrideValue === undefined) {
        return defaultValue
    }

    if (!isResponsiveProps(overrideValue)) {
        return overrideValue
    }

    if (!isResponsiveProps(defaultValue)) {
        return merge({ base: defaultValue }, pickBy(overrideValue, isDefined))
    }

    return merge(defaultValue, pickBy(overrideValue, isDefined))
}

/**
 * Merges two props objects, where override values only replace defaults
 * when they are not undefined. Handles responsive prop objects by merging
 * breakpoint by breakpoint.
 *
 * @example
 * // Simple props
 * mergeResponsiveProps(
 *   { padding: "none", borderless: true },
 *   { padding: "lg" }
 * )
 * // => { padding: "lg", borderless: true }
 *
 * @example
 * // Responsive props - undefined values don't override
 * mergeResponsiveProps(
 *   { padding: "lg" },
 *   { padding: { base: undefined, md: "xl" } }
 * )
 * // => { padding: { base: "lg", md: "xl" } }
 *
 * @example
 * // Merging two responsive objects
 * mergeResponsiveProps(
 *   { gap: { base: "sm", md: "md" } },
 *   { gap: { md: "lg", xl: "xl" } }
 * )
 * // => { gap: { base: "sm", md: "lg", xl: "xl" } }
 */
export function mergeResponsiveProps<TProps extends object>(
    defaultProps: TProps,
    overrideProps?: Partial<TProps>
): TProps {
    if (!overrideProps) {
        return defaultProps
    }

    const finalProps = structuredClone(defaultProps)

    for (const key of Object.keys(overrideProps) as Array<keyof TProps>) {
        const mergedValue = mergeResponsiveValue(defaultProps[key], overrideProps[key])

        if (mergedValue !== undefined) {
            Object.assign(finalProps, { [key]: mergedValue })
        }
    }

    return finalProps
}
